import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Play,
  Square,
  Edit,
  Trash2,
  Eye,
  Repeat,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  MessageSquare,
  Menu,
} from 'lucide-react';
import clsx from 'clsx';
import { useChatLayout } from '../../components/layout/ChatLayout';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  routinesApi,
  type Routine,
} from '../../services/routines-api';
import { handleApiError } from '../../services/api';
import { humanReadableCron } from '../../utils/cron';
import { useTimezone } from '../../contexts/TimezoneContext';
import toast from 'react-hot-toast';

interface RoutineCardProps {
  routine: Routine;
  onEdit: (routine: Routine) => void;
  onDelete: (routine: Routine) => void;
  onToggleStatus: (routine: Routine) => void;
  onViewDetails: (routine: Routine) => void;
  onRunNow: (routine: Routine) => void;
}

const RoutineCard: React.FC<RoutineCardProps> = ({
  routine,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewDetails,
  onRunNow,
}) => {
  const { formatRelativeTime } = useTimezone();
  const isActive = routine.status === 'active';

  const getStatusVariant = () => {
    if (isActive) return 'success';
    if (routine.error_count > 0) return 'error';
    return 'neutral';
  };

  const channelCount = routine.outputChannels?.length ?? 0;

  return (
    <div className="rounded-xl bg-muted border border-border hover:bg-accent/50 dark:hover:bg-white/[0.07] transition-colors">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground">
                {routine.name}
              </h3>
              <Badge variant={getStatusVariant()}>
                {isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
              <MessageSquare className="w-3 h-3 inline mr-1" />
              {routine.message}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {humanReadableCron(routine.schedule)}
              </span>
              <span>{routine.timezone}</span>
              <span>
                Chat{channelCount > 0 ? ` + ${channelCount} channel${channelCount > 1 ? 's' : ''}` : ''}
              </span>
              {routine.last_run && (
                <span>Last run: {formatRelativeTime(routine.last_run)}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" onClick={() => onViewDetails(routine)} title="View Details">
              <Eye className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" onClick={() => onEdit(routine)} title="Edit">
              <Edit className="w-4 h-4" />
            </button>
            <button
              className="p-2 rounded-lg text-primary hover:text-primary hover:bg-accent transition-colors"
              onClick={() => onRunNow(routine)}
              title="Run Now"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              className="p-2 rounded-lg text-destructive hover:text-destructive/80 hover:bg-accent transition-colors"
              onClick={() => onDelete(routine)}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {routine.error_count > 0 && (
          <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                {routine.error_count} error{routine.error_count > 1 ? 's' : ''} occurred
              </span>
            </div>
            {routine.last_error && (
              <p className="text-sm text-destructive mt-1">
                {routine.last_error}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {routine.next_run && (
              <span>Next run: {formatRelativeTime(routine.next_run)}</span>
            )}
          </div>

          <button
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors',
              isActive
                ? 'border-warn/20 text-warn hover:bg-warn/15'
                : 'border-success/20 text-success hover:bg-success/15'
            )}
            onClick={() => onToggleStatus(routine)}
          >
            {isActive ? (
              <>
                <Square className="w-4 h-4" />
                Disable
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Enable
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const RoutinesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMobile, toggleSidebar } = useChatLayout();

  const { data, isLoading, error } = useQuery({
    queryKey: ['routines'],
    queryFn: routinesApi.listRoutines,
    refetchInterval: 30000,
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ routineId, enable }: { routineId: string; enable: boolean }) => {
      return enable
        ? routinesApi.enableRoutine(routineId)
        : routinesApi.disableRoutine(routineId);
    },
    onSuccess: (_, { enable }) => {
      toast.success(`Routine ${enable ? 'enabled' : 'disabled'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['routines'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update routine status');
    },
  });

  const runRoutineMutation = useMutation({
    mutationFn: routinesApi.executeRoutine,
    onSuccess: (result) => {
      const threadId = result.data?.thread_id;
      if (threadId) {
        toast.success('Routine executed — opening chat thread...');
        navigate(`/chat/${threadId}`);
      } else {
        toast.success(result.message || 'Routine execution started');
      }
      queryClient.invalidateQueries({ queryKey: ['routines'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to run routine');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: routinesApi.deleteRoutine,
    onSuccess: () => {
      toast.success('Routine deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['routines'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to delete routine');
    },
  });

  const routines: Routine[] = data?.routines || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-6">
        <h3 className="text-lg font-medium text-destructive mb-2">Error Loading Routines</h3>
        <p className="text-destructive">Failed to load routines. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Repeat className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Routines
            </h1>
            <p className="text-muted-foreground mt-1">
              Saved prompts that an agent runs on a schedule and delivers to chat plus any channels you choose.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            {routines.length} routine{routines.length !== 1 ? 's' : ''} configured
          </p>
          <button onClick={() => navigate('/routines/create')} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors">
            <Plus className="w-4 h-4" />
            New Routine
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-xl bg-muted border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent rounded-lg">
                <Repeat className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold text-foreground">{routines.length}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-muted border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/15 rounded-lg">
                <CheckCircle className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-foreground">
                  {routines.filter(r => r.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-muted border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted/20 rounded-lg">
                <Clock className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-foreground">
                  {routines.filter(r => r.status === 'inactive').length}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-muted border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/15 rounded-lg">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">With Errors</p>
                <p className="text-2xl font-bold text-foreground">
                  {routines.filter(r => r.error_count > 0).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {routines.length === 0 ? (
          <div className="rounded-xl bg-muted border border-border p-12 text-center">
            <Repeat className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No Routines Yet
            </h3>
            <p className="text-muted-foreground mb-6">
              Create your first routine — a saved prompt that runs on a schedule and delivers to chat plus any channels you choose.
            </p>
            <button onClick={() => navigate('/routines/create')} className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors">
              <Plus className="w-4 h-4" />
              Create Your First Routine
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {routines.map((routine) => (
              <RoutineCard
                key={routine.id}
                routine={routine}
                onEdit={(r) => navigate(`/routines/${r.id}/edit`)}
                onDelete={(r) => {
                  if (window.confirm(`Are you sure you want to delete "${r.name}"?`)) {
                    deleteMutation.mutate(r.id);
                  }
                }}
                onToggleStatus={(r) => {
                  toggleStatusMutation.mutate({ routineId: r.id, enable: r.status !== 'active' });
                }}
                onViewDetails={(r) => navigate(`/routines/${r.id}`)}
                onRunNow={(r) => {
                  if (window.confirm('This will run the routine now. Continue?')) {
                    runRoutineMutation.mutate(r.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutinesPage;
